<template>
  <box-default>
    <div class="col-xs-12">
      <div class="default-box pi-page">
        <div class="pi-page__head">
          <h1 class="pi-page__title">Product Intelligence</h1>
          <pi-export-screen screen="Engajamento" :filters="screenFilters" :period="exportPeriodValue"/>
        </div>
        <pi-tabs current="engagement"/>
        <pi-filters v-model="filters" :fields="filterFields" @clear="clearFilters"/>

        <section class="pi-section" aria-labelledby="pi-eng-activity">
          <div class="pi-section__head">
            <h2 id="pi-eng-activity" class="pi-section__title">Usuários ativos e em uso</h2>
            <p class="pi-section__hint">{{ periodText }} · série {{ granularityText }}</p>
            <pi-export-menu title="Usuários ativos e em uso" screen="Engajamento" :columns="seriesColumns" :rows="seriesRows" :envelope="series.envelope"
                            :filters="seriesFilters" :period="exportPeriodValue" :disabled="!points.length || !!series.error"/>
          </div>
          <pi-data-state :loading="series.loading" :error="series.error" :envelope="series.envelope" :empty="seriesEmpty"
                         :show-footer="false" @retry="loadSeries(true)">
            <p v-if="headlineDate" class="pi-muted pi-eng__as-of">{{ headlineDate }}</p>
            <ul class="pi-stats">
              <li v-for="item in headline" :key="item.key" class="pi-stat">
                <span class="pi-stat__head">
                  <span class="pi-stat__label">{{ item.label }}</span>
                  <pi-definition-tooltip :info-key="item.infoKey" :label="item.label" :version="seriesVersion"/>
                </span>
                <span class="pi-stat__value">{{ item.text }}</span>
                <span v-if="item.sub" class="pi-stat__sub">{{ item.sub }}</span>
              </li>
            </ul>
            <div class="pi-chart-grid">
              <div class="pi-chart-block">
                <pi-line-chart title="Ativos: DAU, WAU e MAU" :series="charts.active" :format-label="xLabel"
                               :coverage-from="coverageFrom" :tooltip-extras="hoverExtras.active"/>
              </div>
              <div class="pi-chart-block">
                <pi-line-chart title="Em uso: abriram o sistema" :series="charts.inUse" :format-label="xLabel"
                               :coverage-from="coverageFrom" :tooltip-extras="hoverExtras.inUse"/>
              </div>
              <div class="pi-chart-block">
                <pi-line-chart title="Stickiness: DAU/MAU e WAU/MAU" :series="charts.stickiness" unit="pct" :format-label="xLabel"
                               :coverage-from="coverageFrom" :tooltip-extras="hoverExtras.stickiness"/>
              </div>
              <div class="pi-chart-block">
                <pi-line-chart title="Empresas ativas" :series="charts.companies" :format-label="xLabel"
                               :coverage-from="coverageFrom" :tooltip-extras="hoverExtras.companies"/>
              </div>
            </div>
            <p class="pi-muted">
              Ativo = fez ação significativa (inclui a batida derivada). Em uso = abriu o sistema, mesmo sem agir; o login automático do tablet MarQTab, a cada 15 min, não conta.
              A distância entre as duas curvas aparece em "abriram sem agir", na segmentação abaixo.
            </p>
          </pi-data-state>
        </section>

        <template v-if="!blocked">
          <section class="pi-section" aria-labelledby="pi-eng-seats">
            <div class="pi-section__head">
              <h2 id="pi-eng-seats" class="pi-section__title">Assentos: Active Seats Rate, utilização do contrato e WAS</h2>
              <pi-state-legend/>
              <p class="pi-section__hint">Denominadores separados: habilitados elegíveis e assentos contratados.</p>
              <pi-export-menu title="Assentos" screen="Engajamento" :columns="seatsColumns" :rows="seatsRows" :envelope="seats.envelope"
                              :filters="baseFilters" :period="exportPeriodValue" :disabled="!seatsData.hasData || !!seats.error"/>
            </div>
            <pi-data-state :loading="seats.loading" :error="seats.error" :envelope="seats.envelope" :empty="seatsEmpty"
                           :show-footer="false" @retry="loadSeats(true)">
              <div class="pi-cards">
                <pi-kpi-card v-for="card in seatsData.cards" :key="card.key" :label="card.label" :metric="card.metric"
                             :to="companiesPath" :definition-key="card.definitionKey" :info-key="card.infoKey" :definition-version="seatsVersion"
                             :compare="compare"/>
              </div>
              <ul class="pi-stats">
                <li v-for="count in seatsData.counts" :key="count.key" class="pi-stat">
                  <span class="pi-stat__head">
                    <span class="pi-stat__label">{{ count.label }}</span>
                    <pi-definition-tooltip :info-key="count.infoKey" :label="count.label"/>
                  </span>
                  <span class="pi-stat__value">{{ count.text }}</span>
                </li>
              </ul>
              <div class="pi-eng__subhead">
                <h3 class="pi-subtitle pi-eng__subtitle">Active Seats Rate por corte</h3>
                <pi-definition-tooltip info-key="engagement.seatsCut" label="Active Seats Rate por corte"/>
                <pi-export-menu title="Active Seats Rate por corte" screen="Engajamento" :columns="seatCutColumns" :rows="seatCutRows"
                                :envelope="seats.envelope" :filters="baseFilters" :period="exportPeriodValue" :disabled="!seatCutRows.length"/>
              </div>
              <div class="row">
                <div v-for="breakdown in seatsData.breakdowns" :key="breakdown.key" class="col-xs-12 col-md-4">
                  <h3 class="pi-subtitle">{{ breakdown.label }}</h3>
                  <div v-if="breakdown.rows.length" class="pi-table-wrap">
                    <table class="pi-table">
                      <caption class="pi-sr">Active Seats Rate {{ breakdown.label.toLowerCase() }}</caption>
                      <thead>
                      <tr>
                        <pi-th v-for="column in seatCutHeader" :key="column.key" :column="column"/>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="row in breakdown.rows" :key="row.key">
                        <td>{{ row.label }}</td>
                        <td class="is-num"><pi-metric-cell :cell="row.cell"/></td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                  <p v-else class="pi-muted">Sem recorte para este filtro.</p>
                </div>
              </div>
            </pi-data-state>
          </section>

          <section class="pi-section" aria-labelledby="pi-eng-segments">
            <div class="pi-section__head">
              <h2 id="pi-eng-segments" class="pi-section__title">Segmentação</h2>
              <pi-state-legend/>
              <span class="pi-eng__dimension">
                <span class="pi-toggle" role="group" aria-label="Dimensão da segmentação">
                  <button v-for="option in dimensions" :key="option.value" type="button" class="pi-toggle__btn"
                          :class="{ 'is-on': dimension === option.value }"
                          :aria-pressed="dimension === option.value ? 'true' : 'false'"
                          @click="chooseDimension(option.value)">{{ option.label }}</button>
                </span>
                <pi-definition-tooltip info-key="engagement.dimension" label="Dimensão da segmentação"/>
              </span>
              <pi-export-menu title="Segmentação" screen="Engajamento" :columns="segmentExportColumns" :rows="segmentRows"
                              :envelope="segments.envelope" :filters="segmentFilters" :period="exportPeriodValue"
                              :disabled="!segmentRows.length || !!segments.error"/>
            </div>
            <pi-data-state :loading="segments.loading" :error="segments.error" :envelope="segments.envelope"
                           :empty="segments.loaded && !segmentRows.length" empty-message="Nenhum segmento com dados no período."
                           :show-footer="false" @retry="loadSegments(true)">
              <div class="pi-table-wrap">
                <table class="pi-table">
                  <caption class="pi-sr">Ativos, em uso e abriram sem agir por {{ dimensionText.toLowerCase() }}</caption>
                  <thead>
                  <tr>
                    <pi-th v-for="column in segmentHeader" :key="column.key" :column="column"/>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="row in segmentRows" :key="row.key">
                    <td>{{ row.label }}<sup v-if="row.note" class="pi-muted" :title="row.note">*</sup></td>
                    <td class="is-num"><pi-metric-cell :cell="row.activeUsers"/></td>
                    <td class="is-num"><pi-metric-cell :cell="row.inUseUsers"/></td>
                    <td class="is-num"><pi-metric-cell :cell="row.openedWithoutAction"/></td>
                    <td class="is-num">{{ row.shareText }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <p v-for="row in notedSegmentRows" :key="'note-' + row.key" class="pi-muted">* {{ row.label }}: {{ row.note }}</p>
            </pi-data-state>
          </section>

          <section class="pi-section" aria-labelledby="pi-eng-companies">
            <div class="pi-section__head">
              <h2 id="pi-eng-companies" class="pi-section__title">Empresas: distribuição de ativos e assentos</h2>
              <pi-state-legend/>
              <router-link class="pi-link" :to="companiesRoute">Abrir a lista de empresas</router-link>
              <pi-export-menu title="Empresas" screen="Engajamento" :columns="companyColumns" :source="companySource"
                              :filters="companyExportFilters" :period="exportPeriodValue" :disabled="!companies.loaded || !!companies.error"/>
            </div>
            <pi-company-filters v-model="companyFilters"/>
            <pi-data-state :loading="companies.loading" :error="companies.error" :envelope="companies.envelope"
                           :empty="companiesEmpty" :empty-message="companiesEmptyMessage"
                           :show-footer="false" @retry="loadCompanies(true)">
              <div class="row">
                <div class="col-xs-12 col-md-5">
                  <div class="pi-eng__subhead">
                    <h3 class="pi-subtitle pi-eng__subtitle">Empresas por faixa de usuários ativos (30 dias)</h3>
                    <pi-definition-tooltip info-key="engagement.distributionBand" label="Empresas por faixa de usuários ativos (30 dias)"/>
                    <pi-export-menu title="Empresas por faixa de ativos" screen="Engajamento" :columns="distributionColumns" :rows="distribution"
                                    :envelope="companies.envelope" :filters="companyExportFilters" :period="exportPeriodValue"
                                    :disabled="!distributionTotal"/>
                  </div>
                  <pi-bar-list :rows="distribution" empty-text="Sem distribuição para o recorte." selectable :selected="companyFilters.band"
                               @select="selectBand"/>
                  <p class="pi-muted">{{ distributionTotalText }} Clique numa faixa para filtrar a lista; clique de novo para limpar.</p>
                </div>
                <div class="col-xs-12 col-md-7">
                  <p class="pi-muted pi-eng__list-count">{{ companyCountText }}</p>
                  <p v-if="companyQuiet.note" class="pi-notice">{{ companyQuiet.note }}</p>
                  <framed-table v-if="companyRows.length">
                    <thead>
                    <tr>
                      <template v-for="column in companyHeader">
                        <pi-sort-th v-if="column.sortable" :key="column.key" :column="column" :state="companySort" @sort="sortCompanies"/>
                        <pi-th v-else :key="column.key" :column="column"/>
                      </template>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="row in companyRows" :key="row.key">
                      <td>
                        <pi-company-name :company-id="row.companyId" :shard-code="row.shardCode" :name="row.name"/>
                        <span class="pi-table__sub">{{ row.sizeBandText }} · {{ row.segmentText }}</span>
                        <span v-if="row.journeyText" class="pi-badge pi-badge--muted" title="Jornada do cliente no cadastro (informativo)">{{ row.journeyText }}</span>
                        <span v-if="row.drop" class="pi-eng__drop">
                          <span class="pi-badge pi-badge--bad">Queda de atividade</span>
                          <pi-definition-tooltip info-key="common.signalDrop" label="Queda de atividade"/>
                        </span>
                      </td>
                      <td class="is-num">
                        <span v-if="row.activeMissing" class="pi-unmeasured" :title="row.activeTitle">—<span class="pi-sr"> {{ row.activeTitle }}</span></span>
                        <pi-metric-cell v-else :cell="row.activeUsers"/>
                        <span class="pi-table__sub">em uso {{ row.inUseText }} · habilitados {{ row.enabledText }}</span>
                      </td>
                      <td class="is-num"><pi-metric-cell :cell="row.activeSeatsRate"/></td>
                      <td class="is-num">
                        <pi-metric-cell :cell="row.contractUtilization"/>
                        <span class="pi-table__sub">contratados {{ row.seatsText }}</span>
                      </td>
                      <td><pi-sparkline :points="row.trend" :label="row.name" :width="96" :height="28"/></td>
                    </tr>
                    </tbody>
                    <table-pager slot="footer" :total="companyTotal" :page="companyPage" :page-size="companyPageSize"
                                 @update:page="changeCompanyPage" @update:pageSize="changeCompanyPageSize"/>
                  </framed-table>
                  <p v-else class="pi-muted">Nenhuma empresa nesta página.</p>
                </div>
              </div>
            </pi-data-state>
          </section>
        </template>

        <pi-page-footer :envelopes="envelopes" :labels="envelopeLabels"/>
      </div>
    </div>
  </box-default>
</template>

<script>
import boxDefault from "../../components/layouts/boxDefault.vue";
import framedTable from "../../components/default/framedTable.vue";
import tablePager from "../../components/default/tablePager.vue";
import piTabs from "../../components/pi/piTabs.vue";
import piFilters from "../../components/pi/piFilters.vue";
import piDataState from "../../components/pi/piDataState.vue";
import piKpiCard from "../../components/pi/piKpiCard.vue";
import piLineChart from "../../components/pi/piLineChart.vue";
import piBarList from "../../components/pi/piBarList.vue";
import piSparkline from "../../components/pi/piSparkline.vue";
import piMetricCell from "../../components/pi/piMetricCell.vue";
import piSortTh from "../../components/pi/piSortTh.vue";
import piTh from "../../components/pi/piTh.vue";
import piDefinitionTooltip from "../../components/pi/piDefinitionTooltip.vue";
import piStateLegend from "../../components/pi/piStateLegend.vue";
import piPageFooter from "../../components/pi/piPageFooter.vue";
import piCompanyFilters from "../../components/pi/piCompanyFilters.vue";
import piCompanyName from "../../components/pi/piCompanyName.vue";
import piExportMenu from "../../components/pi/piExportMenu.vue";
import piExportScreen from "../../components/pi/piExportScreen.vue";
import { listFilterState } from "../../components/default/listFilterState";
import { piGet } from "../../components/pi/piApi.js";
import { screenColumns } from "../../components/pi/piColumns.js";
import { exportPeriod, exportProvide, filterLines } from "../../components/pi/piExport.js";
import { formatDate, formatDateFull, formatInteger, formatMonth, formatPeriod } from "../../components/pi/piFormat.js";
import { viewPath } from "../../components/pi/piNavigation.js";
import { indicatorRows } from "../../components/pi/piOverviewModel.js";
import {
  GRANULARITY_OPTIONS, PI_FILTER_DEFAULTS, PI_FILTER_KEY, buildApiQuery, compareEnabled, nextSort, optionLabel,
  previousPeriod, resolvePeriod, toQueryString,
} from "../../components/pi/piQuery.js";
import { blankRequest, isBlocking, listOf, payloadOf, runRequest, shouldLoad } from "../../components/pi/piRequests.js";
import { pageOf, totalOf } from "../../components/pi/piTable.js";
import {
  DEFAULT_COMPANY_SORT, DEFAULT_DIMENSION, ENGAGEMENT_COMPANY_COLUMNS, ENGAGEMENT_DISTRIBUTION_COLUMNS, ENGAGEMENT_SEATS_COLUMNS,
  ENGAGEMENT_SEAT_CUT_COLUMNS, ENGAGEMENT_SERIES_COLUMNS, SEGMENT_DIMENSIONS, distributionRows, distributionTotal, engagementCharts,
  engagementCompanyRows, engagementHeadline, engagementTooltipExtras, latestPoint, seatCounts, seatCutRows, seatsView, segmentColumns,
  segmentRows, sortedPoints, validDimension,
} from "../../components/pi/piEngagementModel.js";
import {
  companyFilterParams, companyFiltersFromQuery, companyFiltersToQuery, hasCompanyFilters, quietSharedPartial, toggleBand,
} from "../../components/pi/piCompanyFilters.js";
import { PI_ENGAGEMENT_COMPANIES, PI_ENGAGEMENT_SEGMENTS, PI_ENGAGEMENT_SERIES, PI_SEATS } from "../../../../urlConfig";

const FILTER_FIELDS = ["period", "compare", "company", "module", "roleClass", "platform", "sizeBand", "segment", "granularity"];
const BASE_FIELDS = ["period", "compare", "company", "module", "roleClass", "platform", "sizeBand", "segment"];
const COMPANY_PAGE_SIZE = 25;
const ENVELOPE_LABELS = ["Série", "Assentos", "Segmentação", "Empresas"];

const keyOf = (url, params) => url + "?" + toQueryString(params);

export default {
  name: "pi-engagement",
  components: {
    boxDefault, framedTable, tablePager, piTabs, piFilters, piDataState, piKpiCard, piLineChart, piBarList, piSparkline,
    piMetricCell, piSortTh, piTh, piDefinitionTooltip, piStateLegend, piPageFooter, piCompanyFilters, piCompanyName, piExportMenu, piExportScreen,
  },
  mixins: [listFilterState(PI_FILTER_KEY, PI_FILTER_DEFAULTS)],
  provide: exportProvide,
  data() {
    return {
      filterFields: FILTER_FIELDS,
      envelopeLabels: ENVELOPE_LABELS,
      series: blankRequest(),
      seats: blankRequest(),
      segments: blankRequest(),
      companies: blankRequest(),
      dimensions: SEGMENT_DIMENSIONS,
      dimension: DEFAULT_DIMENSION,
      seriesColumns: ENGAGEMENT_SERIES_COLUMNS,
      seatsColumns: ENGAGEMENT_SEATS_COLUMNS,
      seatCutColumns: ENGAGEMENT_SEAT_CUT_COLUMNS,
      seatCutHeader: screenColumns(ENGAGEMENT_SEAT_CUT_COLUMNS),
      distributionColumns: ENGAGEMENT_DISTRIBUTION_COLUMNS,
      companyColumns: ENGAGEMENT_COMPANY_COLUMNS,
      companyHeader: screenColumns(ENGAGEMENT_COMPANY_COLUMNS),
      companySort: Object.assign({}, DEFAULT_COMPANY_SORT),
      companyPage: 1,
      companyPageSize: COMPANY_PAGE_SIZE,
      companyFilters: companyFiltersFromQuery(this.$route && this.$route.query),
    };
  },
  computed: {
    compare() {
      return compareEnabled(this.filters);
    },
    period() {
      return resolvePeriod(this.filters, new Date());
    },
    periodText() {
      return formatPeriod(this.period.from, this.period.to);
    },
    granularity() {
      return this.seriesParams.granularity;
    },
    granularityText() {
      return optionLabel(GRANULARITY_OPTIONS, this.granularity).toLowerCase();
    },
    baseParams() {
      return buildApiQuery(this.filters, { fields: BASE_FIELDS });
    },
    baseKey() {
      return toQueryString(this.baseParams);
    },
    seriesParams() {
      return buildApiQuery(this.filters, { fields: FILTER_FIELDS, defaultGranularity: "day" });
    },
    seriesKey() {
      return keyOf(PI_ENGAGEMENT_SERIES, this.seriesParams);
    },
    seatsKey() {
      return keyOf(PI_SEATS, this.baseParams);
    },
    segmentsParams() {
      return buildApiQuery(this.filters, { fields: BASE_FIELDS, extra: { dimension: this.dimension } });
    },
    segmentsKey() {
      return keyOf(PI_ENGAGEMENT_SEGMENTS, this.segmentsParams);
    },
    companiesParams() {
      return buildApiQuery(this.filters, {
        fields: BASE_FIELDS,
        extra: Object.assign(
          { page: this.companyPage, pageSize: this.companyPageSize, sort: this.companySort.sort, desc: this.companySort.desc },
          companyFilterParams(this.companyFilters),
        ),
      });
    },
    companiesKey() {
      return keyOf(PI_ENGAGEMENT_COMPANIES, this.companiesParams);
    },
    companySource() {
      return { path: PI_ENGAGEMENT_COMPANIES, query: this.companiesParams };
    },
    exportPeriodValue() {
      return exportPeriod(this.filters);
    },
    dimensionLine() {
      return "Dimensão da segmentação: " + this.dimensionText;
    },
    seriesFilters() {
      return filterLines({ filters: this.filters, fields: FILTER_FIELDS });
    },
    baseFilters() {
      return filterLines({ filters: this.filters, fields: BASE_FIELDS });
    },
    segmentFilters() {
      return filterLines({ filters: this.filters, fields: BASE_FIELDS, extra: [this.dimensionLine] });
    },
    companyExportFilters() {
      return filterLines({ filters: this.filters, fields: BASE_FIELDS, company: this.companyFilters });
    },
    screenFilters() {
      return filterLines({ filters: this.filters, fields: FILTER_FIELDS, company: this.companyFilters, extra: [this.dimensionLine] });
    },
    blocked() {
      return isBlocking(this.series.error);
    },
    seriesData() {
      return payloadOf(this.series.envelope);
    },
    seriesVersion() {
      return this.series.envelope ? this.series.envelope.definitionVersion : null;
    },
    points() {
      return listOf(this.seriesData.points);
    },
    seriesRows() {
      return sortedPoints(this.points);
    },
    seriesEmpty() {
      return this.series.loaded && !this.points.length;
    },
    charts() {
      return engagementCharts(this.points);
    },
    hoverExtras() {
      return engagementTooltipExtras(this.points, this.granularity);
    },
    coverageFrom() {
      const coverage = this.series.envelope && this.series.envelope.coverage;
      return coverage && coverage.measuredFrom ? coverage.measuredFrom : "";
    },
    headline() {
      return engagementHeadline(this.points);
    },
    headlineDate() {
      const point = latestPoint(this.points);
      if (!point) return "";
      const date = this.granularity === "month" ? formatMonth(point.date) : formatDateFull(point.date);
      return (point.isPartial ? "Último ponto (parcial): " : "Último ponto fechado: ") + date;
    },
    xLabel() {
      return this.granularity === "month" ? formatMonth : formatDate;
    },
    seatsData() {
      return seatsView(payloadOf(this.seats.envelope), this.compare);
    },
    seatsRows() {
      return indicatorRows(this.seatsData.cards, {
        period: this.period,
        previous: previousPeriod(this.period),
        compare: this.compare,
        counts: seatCounts(this.seatsData.counts),
      });
    },
    seatCutRows() {
      return seatCutRows(this.seatsData.breakdowns);
    },
    seatsVersion() {
      return this.seats.envelope ? this.seats.envelope.definitionVersion : null;
    },
    seatsEmpty() {
      return this.seats.loaded && !this.seatsData.hasData;
    },
    dimensionText() {
      const found = SEGMENT_DIMENSIONS.filter((option) => option.value === this.dimension)[0];
      return found ? found.label : "Perfil da empresa";
    },
    segmentDimension() {
      return validDimension(payloadOf(this.segments.envelope).dimension || this.dimension);
    },
    segmentExportColumns() {
      return segmentColumns(this.segmentDimension);
    },
    segmentHeader() {
      return screenColumns(this.segmentExportColumns);
    },
    segmentRows() {
      const data = payloadOf(this.segments.envelope);
      return segmentRows(data.rows, this.segmentDimension, this.compare);
    },
    notedSegmentRows() {
      return this.segmentRows.filter((row) => row.note);
    },
    companiesData() {
      return payloadOf(this.companies.envelope);
    },
    companyQuiet() {
      return quietSharedPartial(engagementCompanyRows(this.companiesData.rows, this.compare),
        ["activeUsers", "activeSeatsRate", "contractUtilization"]);
    },
    companyRows() {
      return this.companyQuiet.rows;
    },
    companyCountText() {
      if (!this.companies.loaded) return "";
      const total = this.companyTotal;
      const text = formatInteger(total) + (total === 1 ? " empresa" : " empresas");
      return hasCompanyFilters(this.companyFilters) ? text + " com os filtros da lista." : text + " na lista.";
    },
    companiesEmptyMessage() {
      return hasCompanyFilters(this.companyFilters) ? "Nenhuma empresa com esses filtros." : "Nenhuma empresa no recorte.";
    },
    companyTotal() {
      return totalOf(this.companiesData, this.companyRows.length);
    },
    distribution() {
      return distributionRows(this.companiesData.distribution);
    },
    distributionTotal() {
      return distributionTotal(this.companiesData.distribution);
    },
    distributionTotalText() {
      return this.distributionTotal ? formatInteger(this.distributionTotal) + " empresas no universo do recorte." : "";
    },
    companiesEmpty() {
      return this.companies.loaded && !this.companyRows.length && !this.distributionTotal;
    },
    companiesPath() {
      return viewPath("companies");
    },
    companiesRoute() {
      return { path: viewPath("companies"), query: Object.assign({}, this.$route.query, companyFiltersToQuery(this.companyFilters)) };
    },
    envelopes() {
      return [this.series.envelope, this.seats.envelope, this.segments.envelope, this.companies.envelope];
    },
  },
  watch: {
    baseKey() {
      this.companyPage = 1;
    },
    seriesKey() {
      this.loadSeries(false);
    },
    seatsKey() {
      this.loadSeats(false);
    },
    segmentsKey() {
      this.loadSegments(false);
    },
    companiesKey() {
      this.loadCompanies(false);
    },
    companyFilters() {
      this.companyPage = 1;
    },
  },
  created() {
    this.loadSeries(false);
    this.loadSeats(false);
    this.loadSegments(false);
    this.loadCompanies(false);
  },
  methods: {
    fetch(slot, url, params, key, action, force) {
      const request = this[slot];
      if (!shouldLoad(request, key, force)) return;
      runRequest(request, () => piGet(url, params), action, key);
    },
    loadSeries(force) {
      this.fetch("series", PI_ENGAGEMENT_SERIES, this.seriesParams, this.seriesKey, "carregar as séries de engajamento", force);
    },
    loadSeats(force) {
      this.fetch("seats", PI_SEATS, this.baseParams, this.seatsKey, "carregar os assentos", force);
    },
    loadSegments(force) {
      this.fetch("segments", PI_ENGAGEMENT_SEGMENTS, this.segmentsParams, this.segmentsKey, "carregar a segmentação", force);
    },
    loadCompanies(force) {
      this.fetch("companies", PI_ENGAGEMENT_COMPANIES, this.companiesParams, this.companiesKey, "carregar as empresas", force);
    },
    chooseDimension(value) {
      this.dimension = validDimension(value);
    },
    selectBand(band) {
      this.companyFilters = toggleBand(this.companyFilters, band);
    },
    sortCompanies(column) {
      this.companySort = nextSort(this.companySort, column.key, column.startDescending);
      this.companyPage = 1;
    },
    changeCompanyPage(page) {
      this.companyPage = pageOf({ page: page }, 1);
    },
    changeCompanyPageSize(size) {
      this.companyPageSize = size;
      this.companyPage = 1;
    },
  },
};
</script>

<style src="../../components/pi/pi.css"></style>

<style scoped>
.pi-page__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  margin-bottom: 12px;
}

.pi-page__head .pi-page__title {
  margin: 0;
}

.pi-eng__as-of {
  margin-bottom: 8px;
}

.pi-eng__subhead {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  margin: 12px 0 8px;
}

.pi-eng__subtitle {
  margin: 0;
}

.pi-eng__dimension {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.pi-eng__drop {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/productIntelligence/piEngagement.vue